<template>
    <div>
        <v-menu
            offset-y
            min-width="150"
            :close-on-content-click="false"
        >
            <template v-slot:activator="{ on }">
                <v-combobox
                    ref="combobox"
                    :value="recipients"
                    chips
                    :items="contactTabs"
                    :item-text="itemText"
                    multiple
                    append-icon=""
                    hide-details
                    :item-value="itemValue"
                    :menu-props="{
                        maxHeight: 0
                    }"
                    :test-id="`task-recipient-selection_${type}`"
                    :error-messages="type === 'to' ? errors.toRecipients : null"
                    dense
                    :class="type === 'to' ? 'email-selection-border' : ''"
                    class="mb-0 pt-0 mt-0 email-selection"
                    v-on="on"
                    @input="recipientAdd"
                >
                    <template v-slot:selection="{ attrs, item, select, selected, index }">
                        <v-tooltip bottom :disabled="!getRecipientError(type, index)">
                            <template v-slot:activator="{ on }">
                                <v-chip
                                    v-bind="attrs"
                                    :input-value="selected"
                                    class="contact-chip pl-4 pr-1"
                                    :class="getRecipientError(type, index) ? 'error--text' : ''"
                                    style="margin-top: 2px; margin-bottom: 2px"
                                    v-on="on"
                                >
                                    {{ itemText(item) }}
                                    <v-btn
                                        icon
                                        text
                                        class="pl-2"
                                        style="margin-top: 2px;"
                                        @click="removeRecipientFromIndex(index)"
                                    >
                                        <v-icon size="20">mdi-close</v-icon>
                                    </v-btn>
                                </v-chip>
                            </template>
                            {{ getRecipientError(type, index) }}
                        </v-tooltip>
                    </template>
                    <template v-slot:prepend-inner>
                        <div
                            class="subtitle-2 text-capitalize ml-2 mt-2"
                            :class="type === 'to' && errors.toRecipients ? 'error--text' : 'n3--text'"
                        >
                            {{ getRecipientTypeTranslation(type) }}
                        </div>
                    </template>
                    <template v-if="type === 'to'" v-slot:append>
                        <v-btn
                            text
                            class="px-2 text-capitalize body-2"
                            style="min-width: unset"
                            :color="isCcActive ? 'primary' : 'n3'"
                            @mouseup.stop
                            @click.prevent.stop="$emit('toggleCc')"
                        >{{ $t("Cc") }}
                        </v-btn>
                        <v-btn
                            text
                            :color="isBccActive ? 'primary' : 'n3'"
                            class="px-2 text-capitalize body-2"
                            style="min-width: unset"
                            @mouseup.stop
                            @click.prevent.stop="$emit('toggleBcc')"
                        >{{ $t("Bcc") }}
                        </v-btn>
                    </template>
                </v-combobox>
            </template>
            <v-card v-if="contactTabs.length">
                <v-card-text class="py-0 px-0 contact-select-wrapper" :class="contactTabs.length > 1 ? 'contact-select-multiple' : ''">
                    <recipient-sub-menu
                        v-if="firstTab"
                        :recipients="recipients"
                        :tab="firstTab"
                        @toggleRecipientFromSelection="toggleRecipientFromSelection($event)"
                    />
                    <v-list v-if="tabs.length" dense>
                        <v-menu
                            v-for="(tab, index) in tabs" :key="index" offset-x right open-on-hover
                            max-height="320"
                        >
                            <template v-slot:activator="{ on }">
                                <v-list-item class="list-item-compact" v-on="on">
                                    <v-list-item-content class="py-2 ml-10">
                                        <v-list-item-title class="font-weight-regular subtitle-2">
                                            {{ tab.name }}
                                        </v-list-item-title>
                                    </v-list-item-content>
                                    <v-list-item-icon>
                                        <v-icon
                                            color="g4"
                                        >
                                            mdi-menu-right
                                        </v-icon>
                                    </v-list-item-icon>
                                </v-list-item>
                            </template>
                            <recipient-sub-menu
                                :recipients="recipients"
                                :tab="tab"
                                show-title
                                @toggleRecipientFromSelection="toggleRecipientFromSelection($event)"
                            />
                        </v-menu>
                    </v-list>
                </v-card-text>
            </v-card>
        </v-menu>
    </div>
</template>

<script>
import { $t } from "@/i18n";
import AppModule from "../../../store/modules/app";
import RecipientSubMenu from "./RecipientSubMenu";
import { ref, computed, watch, onMounted } from "@vue/composition-api";
import Vue from "vue";

export default {
    name: "TaskAddEmailControlEmailSelection",
    components: {
        RecipientSubMenu
    },
    props: {
        recipients: {
            type: Array,
            required: true
        },
        contactTabs: {
            type: Array,
            required: true
        },
        type: {
            type: String,
            required: true
        },
        errors: {
            type: Object,
            default: null
        },
        onlyAutomations: {
            type: Boolean,
            required: true
        },
        accountId: {
            type: Number,
            required: true
        },
        isCcActive: {
            type: Boolean,
            default: false
        },
        isBccActive: {
            type: Boolean,
            default: false
        }
    },
    setup(props, ctx) {
        const { roles } = AppModule();
        const combobox = ref(null);

        const recipientAdd = (data) => {
            let filteredRecipients = []

            for (let currentContact of data) {
                if (typeof currentContact === 'string') {
                    let filteredLine = currentContact.split(/,| /)
                    for (let line of filteredLine) {
                        if (line !== '') {
                            filteredRecipients = filteredRecipients.concat({
                                email: line,
                                type: props.type
                            })
                        }
                    }
                } else {
                    filteredRecipients.push(currentContact)
                }
            }

            ctx.emit('updateRecipients', filteredRecipients)
        }

        const isContactSelected = contact => {
            return props.recipients.find(x => (contact.id && x.id === contact.id) ||
                (contact.custom_type && x.custom_type === contact.custom_type && x.custom_value === contact.custom_value && x.related_type === contact.related_type))
        }

        const toggleRecipientFromSelection = contact => {
            let filteredRecipients = [...props.recipients]

            if (isContactSelected(contact)) {
                if (contact.custom_type) {
                    filteredRecipients = filteredRecipients.filter(x => !(x.custom_type === contact.custom_type && x.custom_value === contact.custom_value && x.related_type === contact.related_type))
                } else {
                    filteredRecipients = filteredRecipients.filter(x => x.email !== contact.email)
                }
            } else {
                filteredRecipients.push(contact)
            }

            ctx.emit('updateRecipients', filteredRecipients)
        }

        const itemText = (item) => {
            if (item.name && item.surname) {
                return `${item.name} ${item.surname}`
            } else if (item.name) {
                return item.name
            }

            return item.email
        }

        const itemValue = (value) => {
            return props.contactTabs.find(x => (value.id && x.id === value.id) || (value.role_id && x.role_id === value.role_id))
        }

        const getRecipientTypeTranslation = type => {
            if (type === 'to') {
                return $t('__To_email')
            } else if (type === 'cc') {
                return $t('__Cc_email')
            } else if (type === 'bcc') {
                return $t('__Bcc_email')
            }
        }

        const getRecipientError = (type, index) => {
            if (!props.errors) {
                return null
            }

            let errorObject = null
            if (type === 'to' && props.errors.toRecipientsValues) {
                errorObject = props.errors.toRecipientsValues
            } else if (type === 'cc' && props.errors.ccRecipientsValues) {
                errorObject = props.errors.ccRecipientsValues
            } else if (type === 'bcc' && props.errors.bccRecipientsValues) {
                errorObject = props.errors.bccRecipientsValues
            }

            if (errorObject && errorObject[index]) {
                return errorObject[index]
            }

            return null
        }

        onMounted(async () => {
            await Vue.nextTick()

            if (!combobox.value) {
                return
            }

            if ((props.type === 'cc' || props.type === 'bcc') && props.recipients.length === 0) {
                combobox.value.focus()
                combobox.value.$el.querySelector('input').click()
            }
        })

        const removeRecipientFromIndex = (index) => {
            let recipients = props.recipients.slice()
            recipients.splice(index, 1)

            ctx.emit('updateRecipients', recipients)
        }

        const firstTab = computed(() => props.contactTabs.length === 1 ? props.contactTabs[0] : props.contactTabs.find(x => x.type === 'main_contacts'))
        const tabs = computed(() => props.contactTabs.length === 1 ? [] : props.contactTabs.filter(x => x.type !== 'main_contacts'))

        return {
            recipientAdd,
            itemText,
            itemValue,
            toggleRecipientFromSelection,
            isContactSelected,
            getRecipientTypeTranslation,
            combobox,
            getRecipientError,
            removeRecipientFromIndex,
            firstTab,
            tabs
        }
    }
}
</script>

<style lang="scss" scoped>
.email-selection {
    ::v-deep input {
        font-weight: 600 !important;
        font-size: 14px !important;
    }
}
.contact-chip {
    background-color: var(--v-n15-base) !important;
    height: 26px !important;
}

.email-selection {
    border-radius: 0 !important;

    ::v-deep fieldset {
        border-bottom: 0 !important;
    }

    ::v-deep .v-input__slot {
        &::before {
            border-color: var(--v-g3-base) !important;
        }
    }

    ::v-deep .v-input__append-inner {
        margin-top: auto !important;
        margin-bottom: auto !important;
    }

    &.email-selection-border {
        border-radius: 4px 4px 0 0 !important;
    }
}

</style>

<style lang="scss">
.v-application .v-menu__content {
    .contact-select-multiple {
        .v-list:first-child {
            padding-bottom: 0 !important;
        }

        .v-list:first-child + .v-list {
            padding-top: 0 !important;
        }
    }

    .list-item-compact {
        min-height: unset;

        .v-list-item__content {
            padding-top: 8px;
            padding-bottom: 8px;
        }

        .v-list-item__icon {
            margin-top: auto;
            margin-bottom: auto;
        }
    }

    .contact-select .v-list-item {
        padding-left: 16px !important;
        padding-right: 24px !important;

        .v-list-item__icon {
            margin-right: 0px !important;

            .v-avatar {
                margin-right: 8px !important;
            }
        }
    }
}
</style>